import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type {
  Document,
  DocumentType,
  OwnerTypeEnum,
  PaginatedDocumentTypeList,
} from '../../../js/api';
import { DocumentsService } from '../../../js/api';
import { useHasPermission } from '../hooks/useHasPermission';
import { fetchAllPages } from '../utils/pagination';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../utils/permissions';
import { useAudience } from '../utils/workflowAccess';
import { Button } from './button';
import {
  DocumentActionDialogs,
  DocumentActionsMenu,
  documentLinkClasses,
  getDocumentCapabilities,
  useCanDownloadDocument,
  useDocumentActions,
} from './document-actions';
import { DOCUMENT_TYPES_QUERY_KEY, DocumentUploadDialog } from './document-upload-dialog';
import { DOCUMENTS_QUERY_KEY } from './documents-tab';

/**
 * In-context access to the documents of ONE type on a context.
 *
 * The Documents tab is where anything gets uploaded and everything is listed; this
 * is for the places where a particular type belongs with the data it describes —
 * the CV beside a clinician's basics, reference letters beside their references.
 * Same endpoint, same capability rules, same dialogs as the tab (document-actions),
 * and the same query key prefix so a Documents-tab upload refreshes these views.
 *
 * Presentation: the document name is a one-click view link; the paperclip menu
 * holds every other action the viewer may take.
 */

type DocumentsOfTypeArgs = {
  /** E.g. 'providers.provider', 'connections.connectedfacility' */
  contextType: string;
  contextId: string;
  /** DocumentType slug, e.g. 'curriculum-vitae' */
  documentTypeSlug: string;
};

export const documentsOfTypeQueryKey = ({
  contextId,
  documentTypeSlug,
}: Pick<DocumentsOfTypeArgs, 'contextId' | 'documentTypeSlug'>) => [
  DOCUMENTS_QUERY_KEY,
  contextId,
  documentTypeSlug,
];

/**
 * Query options for "the documents of one type on a context" — shared by every view
 * of it (a tab's sections, a tab label's missing-documents indicator) so they read
 * one cache entry.
 */
export const documentsOfTypeQueryOptions = ({
  contextType,
  contextId,
  documentTypeSlug,
}: DocumentsOfTypeArgs) => ({
  queryKey: documentsOfTypeQueryKey({ contextId, documentTypeSlug }),
  queryFn: () => fetchDocumentsOfType({ contextType, contextId, documentTypeSlug }),
  enabled: !!contextId,
});

/**
 * Every document of one type on a context (utils/pagination: never the first page
 * passed off as the whole). One shape per query key: the "latest only" views read
 * the same cache entry as the full sections, so they must never store a 1-row page
 * under it — a view that got there first would hide the rest from the others.
 */
const fetchDocumentsOfType = ({ contextType, contextId, documentTypeSlug }: DocumentsOfTypeArgs) =>
  fetchAllPages<Document>((page) =>
    DocumentsService.documentsList({
      contextType,
      contextId,
      documentType: documentTypeSlug,
      ...page,
    })
  );

/** The documents out of a list response, whichever shape it came back in. */
export const documentsFromListResponse = (raw: unknown): Document[] =>
  Array.isArray(raw)
    ? (raw as Document[])
    : ((raw as { results?: Document[] } | undefined)?.results ?? []);

export function useDocumentsOfType({
  contextType,
  contextId,
  documentTypeSlug,
}: DocumentsOfTypeArgs) {
  const query = useQuery(documentsOfTypeQueryOptions({ contextType, contextId, documentTypeSlug }));
  const raw = query.data as unknown;
  const documents: Document[] = Array.isArray(raw)
    ? (raw as Document[])
    : ((raw as { results?: Document[] } | undefined)?.results ?? []);
  return { documents, latest: documents[0], isLoading: query.isLoading, error: query.error };
}

// The download rule lives with the other capability rules (document-actions.tsx) so the
// Documents tab can use it without a circular import; re-exported for existing callers.
export { useCanDownloadDocument };

const linkClasses = documentLinkClasses;

/**
 * One document, in context: its name as a one-click view link (plain text until the
 * upload is finalized) plus the paperclip action menu. The host renders the dialogs
 * once via `DocumentActionDialogs` with the same `actions`.
 */
export function DocumentLink({
  doc,
  viewer,
  actions,
  menu = true,
  text,
  'data-testid': testId,
}: {
  doc: Document;
  viewer: Parameters<typeof getDocumentCapabilities>[1];
  actions: ReturnType<typeof useDocumentActions>;
  /** Render the paperclip beside the name (default); cards place it bottom-right instead. */
  menu?: boolean;
  /** Visible link text; defaults to the label, else the file name. */
  text?: React.ReactNode;
  'data-testid'?: string;
}) {
  const capabilities = getDocumentCapabilities(doc, viewer);
  const name = text ?? (doc.label || doc.original_filename);
  // file names have no spaces to wrap at, so a long one must break anywhere rather
  // than push the card (or its grid cell) wider than the column
  return (
    <span
      className="tw:inline-flex tw:min-w-0 tw:max-w-full tw:items-center tw:gap-1"
      data-testid={testId}
    >
      {capabilities.isFinalized ? (
        <button
          type="button"
          className={`${linkClasses} tw:min-w-0 tw:break-all`}
          onClick={() => actions.openDocument(doc.id, 'inline')}
          disabled={actions.isOpening}
          title="View Document"
          data-testid={`document-link-${doc.id}`}
        >
          {name}
        </button>
      ) : (
        <span className="tw:min-w-0 tw:break-all">{name}</span>
      )}
      {menu && <DocumentMenu doc={doc} viewer={viewer} actions={actions} />}
    </span>
  );
}

/** The paperclip menu on its own, for hosts that anchor it away from the name. */
export function DocumentMenu({
  doc,
  viewer,
  actions,
}: {
  doc: Document;
  viewer: Parameters<typeof getDocumentCapabilities>[1];
  actions: ReturnType<typeof useDocumentActions>;
}) {
  return (
    <DocumentActionsMenu
      doc={doc}
      capabilities={getDocumentCapabilities(doc, viewer)}
      actions={actions}
      trigger="paperclip"
    />
  );
}

type CommonProps = DocumentsOfTypeArgs & {
  /** Who owns uploads here (the acting entity's id); drives the owner-only actions. */
  ownerId: string;
  /** Optional provider the document is about (HCO/staff uploads on a provider). */
  relatedProviderId?: string;
  /** The parent may need to refetch its own queries after a change. */
  onDocumentsChanged?: () => void;
  /** Label for the upload affordance shown when nothing is on file (default "Upload"). */
  uploadLabel?: string;
};

type LatestProps = CommonProps & {
  mode: 'latest';
  /** Rendered when no document of the type exists (e.g. a legacy yes/no). */
  emptyContent?: React.ReactNode;
  'data-testid'?: string;
};

export type DocumentRenderHelpers = {
  /** The upload button for this type (null when the viewer's audience may not upload it). */
  renderUpload: () => React.ReactNode;
  /**
   * The same pinned-type upload as a text link, for an empty state that reads
   * "No <things> on file. Add one." (null when the viewer's audience may not upload it).
   */
  renderUploadLink: (text?: React.ReactNode) => React.ReactNode;
  /** Name as a one-click view link; `menu: false` when the card anchors the paperclip itself. */
  renderLink: (
    doc: Document,
    testId?: string,
    options?: { menu?: boolean; text?: React.ReactNode }
  ) => React.ReactNode;
  /** The paperclip menu alone (e.g. a card's bottom-right slot). */
  renderMenu: (doc: Document) => React.ReactNode;
};

type ListProps = CommonProps & {
  mode: 'list';
  /** The host lays the set out; it gets the documents and renderers for each. */
  children: (documents: Document[], helpers: DocumentRenderHelpers) => React.ReactNode;
};

export function DocumentTypeAccess(props: LatestProps | ListProps) {
  const {
    contextType,
    contextId,
    documentTypeSlug,
    ownerId,
    relatedProviderId,
    onDocumentsChanged,
    uploadLabel = 'Upload',
  } = props;
  const queryClient = useQueryClient();
  const audience = useAudience();
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const viewer = { ownerId, audience, canUpdateSignature };
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  // May this audience upload the type here? Same query the upload dialog uses.
  const { data: documentTypes } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, contextType, audience],
    queryFn: () => DocumentsService.documentsTypesList({ contextTypes: [contextType] }),
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });
  const uploadableType: DocumentType | undefined = useMemo(
    () =>
      (documentTypes ?? []).find(
        (dt) =>
          dt.slug === documentTypeSlug && (dt.allowed_audience_upload ?? []).includes(audience)
      ),
    [documentTypes, documentTypeSlug, audience]
  );

  // `mode: 'latest'` shows only the newest; the fetch is still the full list (see
  // `fetchDocumentsOfType`), the endpoint orders newest first
  const { documents, latest, isLoading } = useDocumentsOfType({
    contextType,
    contextId,
    documentTypeSlug,
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, contextId] });
  const actions = useDocumentActions({
    // the tab's key prefix: refreshes this view, the tab, and any sibling type views
    invalidate,
    onDocumentsChanged,
  });

  const renderUpload = () =>
    uploadableType ? (
      <Button
        color="brand-green-outline"
        small
        onClick={() => setIsUploadOpen(true)}
        title={`Upload ${uploadableType.title}`}
        data-testid={`document-upload-${documentTypeSlug}`}
      >
        <i className="fas fa-fw fa-upload" /> {uploadLabel}
      </Button>
    ) : null;

  const renderUploadLink = (text: React.ReactNode = 'Add one.') =>
    uploadableType ? (
      <button
        type="button"
        className={linkClasses}
        onClick={() => setIsUploadOpen(true)}
        title={`Upload ${uploadableType.title}`}
        data-testid={`document-upload-link-${documentTypeSlug}`}
      >
        {text}
      </button>
    ) : null;

  const helpers: DocumentRenderHelpers = {
    renderUpload,
    renderUploadLink,
    renderLink: (doc, testId, options) => (
      <DocumentLink
        doc={doc}
        viewer={viewer}
        actions={actions}
        menu={options?.menu ?? true}
        text={options?.text}
        data-testid={testId}
      />
    ),
    renderMenu: (doc) => <DocumentMenu doc={doc} viewer={viewer} actions={actions} />,
  };

  let body: React.ReactNode;
  if (props.mode === 'list') {
    body = isLoading ? (
      <span className="tw:text-sm tw:text-zinc-500">Loading documents...</span>
    ) : (
      props.children(documents, helpers)
    );
  } else if (isLoading) {
    body = <span className="tw:text-sm tw:text-zinc-500">…</span>;
  } else if (!latest) {
    // nothing on file: what the host shows for that, plus the upload affordance
    body = (
      <span className="tw:inline-flex tw:flex-wrap tw:items-center tw:gap-2">
        {props.emptyContent ?? null}
        {renderUpload()}
      </span>
    );
  } else {
    body = helpers.renderLink(latest, props['data-testid']);
  }

  return (
    <>
      {body}
      <DocumentActionDialogs actions={actions} audience={audience} />
      {uploadableType && (
        <DocumentUploadDialog
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onSuccess={() => {
            invalidate();
            onDocumentsChanged?.();
          }}
          ownerType={audience as OwnerTypeEnum}
          ownerId={ownerId}
          relatedProviderId={relatedProviderId}
          contextType={contextType}
          contextId={contextId}
          fixedDocumentTypeSlug={documentTypeSlug}
        />
      )}
    </>
  );
}

/** One document type of a group: its documents and whether the viewer may add to it. */
export type DocumentTypeGroup = {
  slug: string;
  /** DocumentType title (falls back to the slug until the types have loaded). */
  title: string;
  documents: Document[];
  canUpload: boolean;
};

export type DocumentGroupRenderHelpers = Pick<
  DocumentRenderHelpers,
  'renderLink' | 'renderMenu'
> & {
  /**
   * May this viewer download the document's file? (`useCanDownloadDocument`)
   */
  canDownload: (doc: Document) => boolean;
  /**
   * Upload pinned to ONE of the group's types — a button (upload-bar shortcut) or a text
   * link ("Add one"). Null when the viewer's audience may not upload that type.
   */
  renderUpload: (
    slug: string,
    options?: { as?: 'button' | 'link'; text?: React.ReactNode }
  ) => React.ReactNode;
};

type DocumentTypesAccessProps = Omit<CommonProps, 'documentTypeSlug' | 'uploadLabel'> & {
  /** The related types to show together, in display order (e.g. the education types). */
  documentTypeSlugs: readonly string[];
  children: (groups: DocumentTypeGroup[], helpers: DocumentGroupRenderHelpers) => React.ReactNode;
};

/**
 * In-context access to SEVERAL related types on a context, as one group — e.g. a tab
 * that owns "the education documents". The host decides the layout from the groups:
 * typically a section of cards per type that has documents, and one upload bar with a
 * shortcut per type that has none, so nobody picks from the full type dropdown and no
 * empty sections are rendered.
 *
 * Same endpoint, capability rules, dialogs and query keys as `DocumentTypeAccess` (one
 * query per type, so a Documents-tab upload refreshes the matching group).
 */
export function DocumentTypesAccess({
  contextType,
  contextId,
  documentTypeSlugs,
  ownerId,
  relatedProviderId,
  onDocumentsChanged,
  children,
}: DocumentTypesAccessProps) {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const viewer = { ownerId, audience, canUpdateSignature };
  const canDownload = useCanDownloadDocument(contextType);
  // the type whose pinned upload dialog is open
  const [uploadSlug, setUploadSlug] = useState<string | null>(null);

  const { data: documentTypes } = useQuery({
    queryKey: [DOCUMENT_TYPES_QUERY_KEY, contextType, audience],
    queryFn: () => DocumentsService.documentsTypesList({ contextTypes: [contextType] }),
    select: (data: PaginatedDocumentTypeList) => data?.results ?? [],
  });

  const queries = useQueries({
    queries: documentTypeSlugs.map((documentTypeSlug) =>
      documentsOfTypeQueryOptions({ contextType, contextId, documentTypeSlug })
    ),
  });
  const isLoading = queries.some((query) => query.isLoading);

  const groups: DocumentTypeGroup[] = documentTypeSlugs.map((slug, index) => {
    const documents = documentsFromListResponse(queries[index]?.data);
    const type = (documentTypes ?? []).find((dt) => dt.slug === slug);
    return {
      slug,
      title: type?.title ?? slug,
      documents,
      canUpload: (type?.allowed_audience_upload ?? []).includes(audience),
    };
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, contextId] });
  const actions = useDocumentActions({ invalidate, onDocumentsChanged });

  const helpers: DocumentGroupRenderHelpers = {
    canDownload,
    renderUpload: (slug, options) => {
      const group = groups.find((candidate) => candidate.slug === slug);
      if (!group?.canUpload) return null;
      const title = `Upload ${group.title}`;
      if (options?.as === 'link') {
        return (
          <button
            type="button"
            className={linkClasses}
            onClick={() => setUploadSlug(slug)}
            title={title}
            data-testid={`document-upload-link-${slug}`}
          >
            {options.text ?? 'Add one.'}
          </button>
        );
      }
      return (
        <Button
          color="brand-green-outline"
          small
          onClick={() => setUploadSlug(slug)}
          title={title}
          data-testid={`document-upload-${slug}`}
        >
          {options?.text ?? (
            <>
              <i className="fas fa-fw fa-plus" /> {group.title}
            </>
          )}
        </Button>
      );
    },
    renderLink: (doc, testId, options) => (
      <DocumentLink
        doc={doc}
        viewer={viewer}
        actions={actions}
        menu={options?.menu ?? true}
        text={options?.text}
        data-testid={testId}
      />
    ),
    renderMenu: (doc) => <DocumentMenu doc={doc} viewer={viewer} actions={actions} />,
  };

  return (
    <>
      {isLoading ? (
        <span className="tw:text-sm tw:text-zinc-500">Loading documents...</span>
      ) : (
        children(groups, helpers)
      )}
      <DocumentActionDialogs actions={actions} audience={audience} />
      {uploadSlug && (
        <DocumentUploadDialog
          // remount per type: the dialog seeds its form from the pinned slug
          key={uploadSlug}
          open
          onClose={() => setUploadSlug(null)}
          onSuccess={() => {
            invalidate();
            onDocumentsChanged?.();
          }}
          ownerType={audience as OwnerTypeEnum}
          ownerId={ownerId}
          relatedProviderId={relatedProviderId}
          contextType={contextType}
          contextId={contextId}
          fixedDocumentTypeSlug={uploadSlug}
        />
      )}
    </>
  );
}
